import React, { useState, useEffect } from 'react';
import {
  Building2, Save, X, Edit2, Search, CheckCircle, AlertTriangle, AlertCircle,
  HelpCircle, Download, Eye, Layers, Users, BookOpen,
  Loader2, RefreshCw
} from 'lucide-react';
import api from '../../api';
import './LicensingManagement.css';

const LicensingManagement = () => {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'EXCEEDED' | 'NEAR_LIMIT' | 'HEALTHY' | 'UNALLOCATED'
  const [sortBy, setSortBy] = useState('UTILIZATION_DESC'); // 'UTILIZATION_DESC' | 'USED_DESC' | 'ALLOCATED_DESC' | 'NAME_ASC'
  const [error, setError] = useState(null);
  const [toasts, setToasts] = useState([]);

  // Adjust Quota Modal State
  const [adjustModalUni, setAdjustModalUni] = useState(null);
  const [newQuotaValue, setNewQuotaValue] = useState(0);
  const [savingQuota, setSavingQuota] = useState(false);

  // Breakdown Modal State
  const [breakdownUniId, setBreakdownUniId] = useState(null);
  const [breakdownData, setBreakdownData] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [breakdownSearch, setBreakdownSearch] = useState('');

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 4500);
  };

  const fetchUniversities = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/university-licenses');
      setUniversities(res.data || []);
    } catch (err) {
      console.error('Failed to fetch university licenses', err);
      setError('Failed to load university licenses.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUniversities();
  }, []);

  // Open Adjust Modal
  const openAdjustModal = (uni) => {
    setAdjustModalUni(uni);
    setNewQuotaValue(uni.lms_licenses || 0);
  };

  const handleSaveQuota = async () => {
    if (!adjustModalUni) return;
    const quotaInt = parseInt(newQuotaValue, 10);
    if (isNaN(quotaInt) || quotaInt < 0) {
      showToast('Please enter a valid non-negative license quota', 'error');
      return;
    }

    setSavingQuota(true);
    try {
      await api.put(`/admin/university/${adjustModalUni.user_id}/licenses`, { lms_licenses: quotaInt });
      await fetchUniversities();
      showToast(`License quota updated for ${adjustModalUni.institution_name}!`, 'success');
      setAdjustModalUni(null);
    } catch (err) {
      console.error('Failed to update licenses', err);
      showToast('Failed to update licenses: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSavingQuota(false);
    }
  };

  // Open Breakdown Drilldown Modal
  const openBreakdownModal = async (uni) => {
    setBreakdownUniId(uni.user_id);
    setBreakdownData(null);
    setBreakdownSearch('');
    setLoadingBreakdown(true);
    try {
      const res = await api.get(`/admin/university/${uni.user_id}/license-breakdown`);
      setBreakdownData(res.data);
    } catch (err) {
      console.error('Failed to load license breakdown', err);
      showToast('Failed to load license breakdown details', 'error');
      setBreakdownUniId(null);
    } finally {
      setLoadingBreakdown(false);
    }
  };

  // CSV Export
  const handleExportCsv = () => {
    if (universities.length === 0) return;
    const headers = ['University Name', 'Contact Email', 'Licenses Allocated', 'Licenses Used', 'Remaining', 'Utilization %', 'Status'];
    const rows = universities.map(u => {
      const isExceeded = u.used_licenses > u.lms_licenses;
      const statusStr = u.lms_licenses === 0 ? 'Unallocated' : isExceeded ? 'Exceeded' : u.utilization_rate >= 90 ? 'Near Limit' : 'Healthy';
      return [
        `"${(u.institution_name || '').replace(/"/g, '""')}"`,
        `"${u.email || ''}"`,
        u.lms_licenses || 0,
        u.used_licenses || 0,
        Math.max(0, (u.lms_licenses || 0) - (u.used_licenses || 0)),
        `${u.utilization_rate || 0}%`,
        statusStr
      ].join(',');
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `university_licenses_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('License roster exported to CSV successfully!');
  };

  // Summary KPIs Calculation
  const totalUniversities = universities.length;
  const totalAllocated = universities.reduce((acc, u) => acc + (u.lms_licenses || 0), 0);
  const totalUsed = universities.reduce((acc, u) => acc + (u.used_licenses || 0), 0);
  const overallUtilization = totalAllocated > 0 ? Math.round((totalUsed / totalAllocated) * 100) : 0;
  const atRiskCount = universities.filter(u => u.lms_licenses > 0 && ((u.used_licenses / u.lms_licenses) >= 0.9)).length;

  const countExceeded = universities.filter(u => u.used_licenses > u.lms_licenses).length;
  const countNearLimit = universities.filter(u => u.lms_licenses > 0 && u.used_licenses <= u.lms_licenses && (u.used_licenses / u.lms_licenses) >= 0.9).length;
  const countHealthy = universities.filter(u => u.lms_licenses > 0 && (u.used_licenses / u.lms_licenses) < 0.9).length;
  const countUnallocated = universities.filter(u => !u.lms_licenses || u.lms_licenses === 0).length;

  // Filter & Sort
  const filteredUniversities = universities
    .filter(u => {
      const matchSearch = (u.institution_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (u.email || '').toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchSearch) return false;

      const isExceeded = u.used_licenses > u.lms_licenses;
      const isNearLimit = u.lms_licenses > 0 && !isExceeded && ((u.used_licenses / u.lms_licenses) >= 0.9);
      const isHealthy = u.lms_licenses > 0 && ((u.used_licenses / u.lms_licenses) < 0.9);
      const isUnallocated = !u.lms_licenses || u.lms_licenses === 0;

      if (statusFilter === 'EXCEEDED') return isExceeded;
      if (statusFilter === 'NEAR_LIMIT') return isNearLimit;
      if (statusFilter === 'HEALTHY') return isHealthy;
      if (statusFilter === 'UNALLOCATED') return isUnallocated;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'UTILIZATION_DESC') return (b.utilization_rate || 0) - (a.utilization_rate || 0);
      if (sortBy === 'USED_DESC') return (b.used_licenses || 0) - (a.used_licenses || 0);
      if (sortBy === 'ALLOCATED_DESC') return (b.lms_licenses || 0) - (a.lms_licenses || 0);
      if (sortBy === 'NAME_ASC') return (a.institution_name || '').localeCompare(b.institution_name || '');
      return 0;
    });

  return (
    <div className="licensing-container">
      {/* ── Top Header ── */}
      <div className="licensing-header">
        <div className="licensing-title-wrap">
          <h2>
            <Building2 size={28} style={{ color: 'var(--primary, #19D3FF)' }} />
            University LMS Licensing Management
          </h2>
          <p>Monitor quota allocation, active student enrollment consumption, and course mappings across partner institutions.</p>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            type="button"
            className="licensing-export-btn"
            onClick={fetchUniversities}
            disabled={loading}
            title="Refresh licensing roster"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            type="button"
            className="licensing-export-btn"
            onClick={handleExportCsv}
            disabled={universities.length === 0}
            title="Download CSV report"
          >
            <Download size={15} /> Export CSV
          </button>
        </div>
      </div>

      {/* ── Guidance & Rule Banner ── */}
      <div className="licensing-info-banner">
        <HelpCircle size={22} className="licensing-info-icon" />
        <div className="licensing-info-content">
          <div className="licensing-info-title">How LMS Licensing Works & Rule Enforcement</div>
          <p className="licensing-info-desc">
            <strong>1 License = 1 Student Enrolled in an LMS Partner Course.</strong> When a university assigns an EdVergenceX course to an academic cohort (e.g., <em>UG - B.Tech - CSE 2024-2028</em>), licenses corresponding to the number of students in that cohort are consumed. Universities with depleted or exceeded quotas are automatically prevented from assigning new courses until additional licenses are allocated.
          </p>
        </div>
      </div>

      {/* ── KPI Overview Cards ── */}
      <div className="licensing-kpi-grid">
        <div className="licensing-kpi-card">
          <div className="licensing-kpi-icon" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
            <Building2 size={24} />
          </div>
          <div className="licensing-kpi-body">
            <span className="licensing-kpi-val">{totalUniversities}</span>
            <span className="licensing-kpi-label">Partner Universities</span>
            <span className="licensing-kpi-sub">{countUnallocated} unallocated</span>
          </div>
        </div>

        <div className="licensing-kpi-card">
          <div className="licensing-kpi-icon" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>
            <Layers size={24} />
          </div>
          <div className="licensing-kpi-body">
            <span className="licensing-kpi-val">{totalAllocated.toLocaleString()}</span>
            <span className="licensing-kpi-label">Total Quota Allocated</span>
            <span className="licensing-kpi-sub">Platform-wide pool</span>
          </div>
        </div>

        <div className="licensing-kpi-card">
          <div className="licensing-kpi-icon" style={{ background: 'rgba(25, 211, 255, 0.12)', color: 'var(--primary, #19D3FF)' }}>
            <Users size={24} />
          </div>
          <div className="licensing-kpi-body">
            <span className="licensing-kpi-val">{totalUsed.toLocaleString()}</span>
            <span className="licensing-kpi-label">Licenses Consumed</span>
            <span className="licensing-kpi-sub">{overallUtilization}% overall utilization</span>
          </div>
        </div>

        <div className="licensing-kpi-card">
          <div className="licensing-kpi-icon" style={{ background: atRiskCount > 0 ? 'rgba(239, 68, 68, 0.12)' : 'rgba(148, 163, 184, 0.12)', color: atRiskCount > 0 ? '#ef4444' : '#64748b' }}>
            <AlertTriangle size={24} />
          </div>
          <div className="licensing-kpi-body">
            <span className="licensing-kpi-val" style={{ color: atRiskCount > 0 ? '#ef4444' : 'inherit' }}>{atRiskCount}</span>
            <span className="licensing-kpi-label">At-Risk Institutions</span>
            <span className="licensing-kpi-sub">{countExceeded} exceeded, {countNearLimit} near limit</span>
          </div>
        </div>
      </div>

      {/* ── Filters & Controls Bar ── */}
      <div className="licensing-controls-bar">
        {/* Status Tab Pills */}
        <div className="licensing-tabs">
          <button
            type="button"
            className={`licensing-tab-pill ${statusFilter === 'ALL' ? 'active' : ''}`}
            onClick={() => setStatusFilter('ALL')}
          >
            All Institutions ({totalUniversities})
          </button>
          <button
            type="button"
            className={`licensing-tab-pill ${statusFilter === 'EXCEEDED' ? 'active' : ''}`}
            onClick={() => setStatusFilter('EXCEEDED')}
            style={countExceeded > 0 ? { borderColor: '#ef4444', color: statusFilter === 'EXCEEDED' ? '#fff' : '#ef4444' } : {}}
          >
            Over Quota ({countExceeded})
          </button>
          <button
            type="button"
            className={`licensing-tab-pill ${statusFilter === 'NEAR_LIMIT' ? 'active' : ''}`}
            onClick={() => setStatusFilter('NEAR_LIMIT')}
            style={countNearLimit > 0 ? { borderColor: '#f59e0b', color: statusFilter === 'NEAR_LIMIT' ? '#fff' : '#f59e0b' } : {}}
          >
            Near Limit ({countNearLimit})
          </button>
          <button
            type="button"
            className={`licensing-tab-pill ${statusFilter === 'HEALTHY' ? 'active' : ''}`}
            onClick={() => setStatusFilter('HEALTHY')}
          >
            Healthy ({countHealthy})
          </button>
          <button
            type="button"
            className={`licensing-tab-pill ${statusFilter === 'UNALLOCATED' ? 'active' : ''}`}
            onClick={() => setStatusFilter('UNALLOCATED')}
          >
            Unallocated ({countUnallocated})
          </button>
        </div>

        {/* Search & Sort Row */}
        <div className="licensing-actions-row">
          <div className="licensing-search-wrapper">
            <Search size={18} style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Search by university name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="licensing-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}
              >
                ×
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.84rem', color: 'var(--text-muted)', fontWeight: 600 }}>Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="licensing-sort-select"
            >
              <option value="UTILIZATION_DESC">Highest Utilization %</option>
              <option value="USED_DESC">Most Licenses Consumed</option>
              <option value="ALLOCATED_DESC">Highest Quota Allocated</option>
              <option value="NAME_ASC">University Name (A-Z)</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Main Data Table ── */}
      {loading ? (
        <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader2 size={36} className="animate-spin" style={{ margin: '0 auto 14px', color: 'var(--primary)' }} />
          <div>Loading university licenses...</div>
        </div>
      ) : error ? (
        <div style={{ padding: '50px 20px', textAlign: 'center', color: '#ef4444', background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid #ef4444' }}>
          <AlertCircle size={32} style={{ margin: '0 auto 10px' }} />
          <div>{error}</div>
          <button onClick={fetchUniversities} style={{ marginTop: '14px', padding: '8px 16px', borderRadius: '8px', background: 'var(--bg-soft)', border: '1px solid var(--border)', color: 'var(--text-main)', cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      ) : (
        <div className="licensing-table-card">
          <div className="licensing-table-responsive">
            <table className="licensing-table">
              <thead>
                <tr>
                  <th style={{ minWidth: '220px' }}>University Institution</th>
                  <th style={{ textAlign: 'center', minWidth: '120px' }}>Quota Allocated</th>
                  <th style={{ textAlign: 'center', minWidth: '130px' }}>Licenses Consumed</th>
                  <th style={{ minWidth: '160px' }}>Utilization</th>
                  <th style={{ textAlign: 'center', minWidth: '100px' }}>Remaining</th>
                  <th style={{ textAlign: 'center', minWidth: '110px' }}>Status</th>
                  <th className="licensing-col-actions" style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
            <tbody>
              {filteredUniversities.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    <Building2 size={36} style={{ color: 'var(--text-faint)', margin: '0 auto 10px' }} />
                    <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>No universities match your filters</div>
                    <div style={{ fontSize: '0.84rem', marginTop: '4px' }}>Try resetting search or selecting "All Institutions"</div>
                  </td>
                </tr>
              ) : (
                filteredUniversities.map(uni => {
                  const allocated = uni.lms_licenses || 0;
                  const used = uni.used_licenses || 0;
                  const remaining = Math.max(0, allocated - used);
                  const isExceeded = used > allocated;
                  const percentUsed = allocated > 0 ? Math.round((used / allocated) * 100) : (used > 0 ? 100 : 0);

                  // Progress bar color
                  let barColor = '#10b981';
                  if (isExceeded) barColor = '#ef4444';
                  else if (percentUsed >= 90) barColor = '#f59e0b';
                  else if (percentUsed >= 75) barColor = '#3b82f6';

                  return (
                    <tr key={uni.user_id}>
                      <td>
                        <div className="licensing-uni-name">{uni.institution_name}</div>
                        <div className="licensing-uni-sub">
                          <span>{uni.email || `ID #${uni.user_id}`}</span>
                          {uni.total_students > 0 && (
                            <>
                              <span>•</span>
                              <span>{uni.total_students} students</span>
                            </>
                          )}
                          {uni.total_assignments > 0 && (
                            <>
                              <span>•</span>
                              <span>{uni.total_assignments} course mapped</span>
                            </>
                          )}
                        </div>
                      </td>

                      <td style={{ textAlign: 'center' }}>
                        <span style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text-main)' }}>
                          {allocated.toLocaleString()}
                        </span>
                      </td>

                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="licensing-used-clickable"
                          onClick={() => openBreakdownModal(uni)}
                          title="Click to view breakdown of course & batch assignments"
                        >
                          <Users size={14} style={{ color: 'var(--primary)' }} />
                          <span>{used.toLocaleString()}</span>
                          <Eye size={12} style={{ opacity: 0.6 }} />
                        </button>
                      </td>

                      <td>
                        <div className="licensing-progress-wrap">
                          <div className="licensing-progress-bar">
                            <div
                              className="licensing-progress-fill"
                              style={{ width: `${Math.min(100, percentUsed)}%`, background: barColor }}
                            />
                          </div>
                          <div className="licensing-progress-label">
                            <span style={{ color: isExceeded ? '#ef4444' : 'inherit' }}>
                              {percentUsed}% used
                            </span>
                            <span>{used} / {allocated}</span>
                          </div>
                        </div>
                      </td>

                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          fontWeight: 700,
                          color: isExceeded ? '#ef4444' : remaining <= 20 && allocated > 0 ? '#f59e0b' : 'var(--text-main)'
                        }}>
                          {isExceeded ? `-${used - allocated}` : remaining.toLocaleString()}
                        </span>
                      </td>

                      <td style={{ textAlign: 'center' }}>
                        {allocated === 0 ? (
                          <span className="licensing-badge licensing-badge-unallocated">Unallocated</span>
                        ) : isExceeded ? (
                          <span className="licensing-badge licensing-badge-exceeded">
                            <AlertCircle size={12} /> Exceeded
                          </span>
                        ) : percentUsed >= 90 ? (
                          <span className="licensing-badge licensing-badge-near-limit">
                            <AlertTriangle size={12} /> Near Limit
                          </span>
                        ) : (
                          <span className="licensing-badge licensing-badge-healthy">
                            <CheckCircle size={12} /> Healthy
                          </span>
                        )}
                      </td>

                      <td className="licensing-col-actions" style={{ textAlign: 'right' }}>
                        <div className="licensing-actions-wrap">
                          <button
                            type="button"
                            className="licensing-btn-adjust"
                            onClick={() => openAdjustModal(uni)}
                            title="Adjust license quota"
                          >
                            <Edit2 size={13} /> Adjust Quota
                          </button>
                          <button
                            type="button"
                            className="licensing-btn-view"
                            onClick={() => openBreakdownModal(uni)}
                            title="View assignment breakdown"
                            aria-label="View assignment breakdown"
                          >
                            <Eye size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* ── MODAL 1: ADJUST LICENSE QUOTA ── */}
      {adjustModalUni && (
        <div className="licensing-modal-overlay" onClick={() => setAdjustModalUni(null)}>
          <div className="licensing-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="licensing-modal-header">
              <h3>
                <Edit2 size={18} style={{ color: 'var(--primary)' }} />
                Adjust License Quota
              </h3>
              <button
                type="button"
                className="licensing-modal-close"
                onClick={() => setAdjustModalUni(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="licensing-modal-body">
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main)' }}>
                  {adjustModalUni.institution_name}
                </div>
                <div style={{ fontSize: '0.84rem', color: 'var(--text-muted)' }}>
                  Current allocation: <strong>{adjustModalUni.lms_licenses || 0}</strong> licenses • Active consumption: <strong>{adjustModalUni.used_licenses || 0}</strong> licenses
                </div>
              </div>

              <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '6px' }}>
                New License Quota:
              </label>

              <input
                type="number"
                min={adjustModalUni.used_licenses || 0}
                value={newQuotaValue}
                onChange={(e) => setNewQuotaValue(e.target.value)}
                className="licensing-input-number"
                placeholder="Enter license amount..."
                autoFocus
              />

              {/* Quick Preset Add Buttons */}
              <div style={{ marginTop: '12px' }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                  Quick Top-Up Presets:
                </span>
                <div className="licensing-adjust-presets">
                  {[50, 100, 250, 500].map(amt => (
                    <button
                      key={amt}
                      type="button"
                      className="licensing-preset-chip"
                      onClick={() => setNewQuotaValue(prev => (parseInt(prev, 10) || 0) + amt)}
                    >
                      +{amt} Licenses
                    </button>
                  ))}
                  <button
                    type="button"
                    className="licensing-preset-chip"
                    onClick={() => setNewQuotaValue(adjustModalUni.used_licenses || 0)}
                    title="Set quota equal to current active consumption"
                  >
                    Set to Exact Usage ({adjustModalUni.used_licenses || 0})
                  </button>
                </div>
              </div>

              {/* Live Preview Box */}
              {(() => {
                const parsedNew = parseInt(newQuotaValue, 10) || 0;
                const used = adjustModalUni.used_licenses || 0;
                const newRem = parsedNew - used;
                const isUnder = parsedNew < used;

                return (
                  <div>
                    {isUnder && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f59e0b', fontSize: '0.84rem', fontWeight: 600, marginTop: '8px' }}>
                        <AlertTriangle size={15} />
                        New quota is lower than current usage ({used}). Access will be capped to {parsedNew} in batch order.
                      </div>
                    )}

                    <div className="licensing-preview-box">
                      <div className="licensing-preview-stat">
                        <span className="licensing-preview-stat-lbl">New Quota</span>
                        <span className="licensing-preview-stat-val">{parsedNew.toLocaleString()}</span>
                      </div>
                      <div className="licensing-preview-stat">
                        <span className="licensing-preview-stat-lbl">Active Used</span>
                        <span className="licensing-preview-stat-val">{used.toLocaleString()}</span>
                      </div>
                      <div className="licensing-preview-stat">
                        <span className="licensing-preview-stat-lbl">New Remaining</span>
                        <span
                          className="licensing-preview-stat-val"
                          style={{ color: newRem >= 0 ? '#10b981' : '#f59e0b' }}
                        >
                          {Math.max(0, newRem).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="licensing-modal-footer">
              <button
                type="button"
                onClick={() => setAdjustModalUni(null)}
                style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-main)', cursor: 'pointer', fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveQuota}
                disabled={savingQuota || isNaN(parseInt(newQuotaValue, 10)) || parseInt(newQuotaValue, 10) < 0}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '9px 20px', borderRadius: '8px', border: 'none',
                  background: 'var(--primary, #05a8f3)', color: '#ffffff',
                  fontWeight: 700, cursor: savingQuota ? 'not-allowed' : 'pointer'
                }}
              >
                {savingQuota ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {savingQuota ? 'Updating...' : 'Save New Quota'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL 2: USAGE BREAKDOWN DRILLDOWN ── */}
      {breakdownUniId && (
        <div className="licensing-modal-overlay" onClick={() => setBreakdownUniId(null)}>
          <div className="licensing-modal-box" style={{ maxWidth: '780px' }} onClick={(e) => e.stopPropagation()}>
            <div className="licensing-modal-header">
              <h3>
                <BookOpen size={20} style={{ color: 'var(--primary)' }} />
                License Consumption Breakdown
              </h3>
              <button
                type="button"
                className="licensing-modal-close"
                onClick={() => setBreakdownUniId(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="licensing-modal-body">
              {loadingBreakdown ? (
                <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <Loader2 size={32} className="animate-spin" style={{ margin: '0 auto 12px', color: 'var(--primary)' }} />
                  <div>Loading assignment breakdown details...</div>
                </div>
              ) : !breakdownData ? (
                <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
                  No breakdown data available.
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-main)' }}>
                      {breakdownData.institution_name}
                    </div>
                    <div style={{ fontSize: '0.84rem', color: 'var(--text-muted)' }}>
                      Complete breakdown of student cohort enrollments mapping to LMS partner courses.
                    </div>
                  </div>

                  {/* Summary Metric Strip */}
                  <div className="licensing-breakdown-stats">
                    <div className="licensing-bstat">
                      <span className="licensing-bstat-val">{breakdownData.lms_licenses || 0}</span>
                      <span className="licensing-bstat-lbl">Total Quota</span>
                    </div>
                    <div className="licensing-bstat">
                      <span className="licensing-bstat-val" style={{ color: 'var(--primary)' }}>{breakdownData.used_licenses || 0}</span>
                      <span className="licensing-bstat-lbl">Licenses Consumed</span>
                    </div>
                    <div className="licensing-bstat">
                      <span className="licensing-bstat-val" style={{ color: '#10b981' }}>{breakdownData.remaining_licenses || 0}</span>
                      <span className="licensing-bstat-lbl">Remaining Available</span>
                    </div>
                    <div className="licensing-bstat">
                      <span className="licensing-bstat-val">{breakdownData.total_students || 0}</span>
                      <span className="licensing-bstat-lbl">Total Registered Students</span>
                    </div>
                  </div>

                  {/* Search within assignments */}
                  <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-soft)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0 12px', marginBottom: '14px' }}>
                    <Search size={16} style={{ color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      placeholder="Filter by course ID or batch..."
                      value={breakdownSearch}
                      onChange={(e) => setBreakdownSearch(e.target.value)}
                      style={{ border: 'none', background: 'transparent', padding: '8px 10px', outline: 'none', width: '100%', color: 'var(--text-main)', fontSize: '0.84rem' }}
                    />
                    {breakdownSearch && (
                      <button onClick={() => setBreakdownSearch('')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
                    )}
                  </div>

                  {/* Assignments Table */}
                  {(() => {
                    const filteredAssignments = (breakdownData.assignments || []).filter(a => {
                      const q = breakdownSearch.toLowerCase();
                      return (a.course_id || '').toLowerCase().includes(q) ||
                             (a.academic_batch || '').toLowerCase().includes(q) ||
                             (a.faculty_name || '').toLowerCase().includes(q);
                    });

                    if (filteredAssignments.length === 0) {
                      return (
                        <div style={{ padding: '36px 16px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-soft)', borderRadius: '10px' }}>
                          <BookOpen size={30} style={{ color: 'var(--text-faint)', margin: '0 auto 8px' }} />
                          <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>No Course Batch Assignments Found</div>
                          <div style={{ fontSize: '0.82rem', marginTop: '4px' }}>
                            This university has not assigned any partner courses to student cohorts yet.
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div style={{ border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
                        <table className="licensing-assignments-table">
                          <thead>
                            <tr>
                              <th>Partner Course ID</th>
                              <th>Target Cohort / Batch</th>
                              <th style={{ textAlign: 'center' }}>Students Enrolled (= Licenses)</th>
                              <th>Assigned Faculty</th>
                              <th>Assigned Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredAssignments.map((a, idx) => (
                              <tr key={a.id || idx}>
                                <td style={{ fontWeight: 700 }}>{a.course_id}</td>
                                <td>
                                  <span className="licensing-batch-pill">
                                    {a.academic_batch}
                                  </span>
                                </td>
                                <td style={{ textAlign: 'center', fontWeight: 800 }}>
                                  <div>{a.students_enrolled}</div>
                                  {a.batch_total_students !== undefined && a.batch_total_students > a.students_enrolled && (
                                    <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', fontWeight: 500, marginTop: '2px' }}>
                                      of {a.batch_total_students} students
                                    </div>
                                  )}
                                </td>
                                <td style={{ color: 'var(--text-muted)' }}>
                                  {a.faculty_name || '—'}
                                </td>
                                <td style={{ color: 'var(--text-muted)' }}>
                                  {a.assigned_at ? new Date(a.assigned_at).toLocaleDateString() : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            <div className="licensing-modal-footer">
              <button
                type="button"
                onClick={() => setBreakdownUniId(null)}
                style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)', cursor: 'pointer', fontWeight: 600 }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast Notifications ── */}
      <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 99999, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={{
              background: toast.type === 'success' ? '#10b981' : '#ef4444',
              color: 'white', padding: '14px 20px', borderRadius: '10px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              display: 'flex', alignItems: 'center', gap: '12px', minWidth: '300px',
              fontWeight: 600, fontSize: '0.9rem'
            }}
          >
            <span>{toast.type === 'success' ? '✓' : '✕'}</span>
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 0 }}
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LicensingManagement;
